import React, { useMemo } from 'react';
import { FieldDefinitions, FieldDefinition, FieldWidth } from '../../types/schema';
import { formatLlmInstructions } from '../../utils/formatLlmInstructions';
import TextField from './fields/TextField';
import SelectField from './fields/SelectField';
import NumberField from './fields/NumberField';
import TextAreaField from './fields/TextAreaField';
import DateField from './fields/DateField';

// Map width values to Tailwind CSS classes
const WIDTH_CLASSES: Record<FieldWidth, string> = {
  'full': 'w-full',
  '1/2': 'w-1/2',
  '1/3': 'w-1/3',
  '1/4': 'w-1/4',
  '2/3': 'w-2/3',
  '3/4': 'w-3/4',
};

interface GenericDynamicFormProps {
  fieldDefinitions: FieldDefinitions;
  data: Record<string, any>;
  provenance?: Record<string, { snippet: string; page: number }>;
  fieldConfidences?: Record<string, { confidence: 'high' | 'medium' | 'low'; reason?: string | null }>;
  onChange: (data: Record<string, any>) => void;
  onSubmit: (e: React.FormEvent) => void;
  isSubmitting?: boolean;
  onProvenanceClick?: (fieldPath: string) => void;
}

interface SectionFields {
  sectionKey: string;
  sectionLabel: string;
  fields: Array<{
    fieldName: string;
    definition: FieldDefinition;
  }>;
}

export function GenericDynamicForm({
  fieldDefinitions,
  data,
  provenance,
  fieldConfidences,
  onChange,
  onSubmit,
  isSubmitting = false,
  onProvenanceClick,
}: GenericDynamicFormProps) {
  // Group fields by section and sort
  const sections = useMemo(() => {
    const sectionMap = new Map<string, SectionFields>();

    // Initialize sections from section config
    Object.entries(fieldDefinitions.sections).forEach(([key, config]) => {
      sectionMap.set(key, {
        sectionKey: key,
        sectionLabel: config.label_fr,
        fields: [],
      });
    });

    // Add fields to their sections
    Object.entries(fieldDefinitions.fields).forEach(([fieldName, definition]) => {
      const sectionKey = definition.ui.section;
      const section = sectionMap.get(sectionKey);

      if (section) {
        section.fields.push({ fieldName, definition });
      } else {
        // Fallback section for fields without a defined section
        if (!sectionMap.has('other')) {
          sectionMap.set('other', {
            sectionKey: 'other',
            sectionLabel: 'Autres',
            fields: [],
          });
        }
        sectionMap.get('other')!.fields.push({ fieldName, definition });
      }
    });

    // Sort fields within each section by display_order
    sectionMap.forEach((section) => {
      section.fields.sort((a, b) =>
        a.definition.ui.display_order - b.definition.ui.display_order
      );
    });

    // Convert to array and sort sections by their order
    return Array.from(sectionMap.values())
      .filter((section) => section.fields.length > 0)
      .sort((a, b) => {
        const orderA = fieldDefinitions.sections[a.sectionKey]?.order ?? 999;
        const orderB = fieldDefinitions.sections[b.sectionKey]?.order ?? 999;
        return orderA - orderB;
      });
  }, [fieldDefinitions]);

  const handleFieldChange = (fieldName: string, value: any) => {
    onChange({ ...data, [fieldName]: value });
  };

  const renderField = (fieldName: string, definition: FieldDefinition) => {
    const value = data[fieldName];
    const fieldProvenance = provenance?.[fieldName];
    const fieldConfidence = fieldConfidences?.[fieldName];
    const { ui, llm } = definition;

    const llmInstructions = formatLlmInstructions(llm);

    const commonProps = {
      name: fieldName,
      label: ui.label_fr,
      fieldName: fieldName, // Pass field name for display
      llmInstructions: llmInstructions || undefined,
      value: value ?? '',
      onChange: handleFieldChange, // Pass function directly (signature: (name, value) => void)
      required: definition.required,
      provenance: fieldProvenance,
      confidence: fieldConfidence,
      onProvenanceClick: onProvenanceClick ? () => onProvenanceClick(fieldName) : undefined,
    };

    // Select input type based on ui.input_type or field type.
    // PROD1.8 : si le schéma déclare `type: 'number'`, on force NumberField
    // peu importe `ui.input_type` — évite que "0" soit posté en string et
    // rejeté par la validation JSON Schema (cf. bug `adjustement`).
    if (definition.type === 'number' || definition.type === 'integer') {
      return <NumberField {...commonProps} />;
    }

    switch (ui.input_type) {
      case 'select':
      case 'dropdown':
        // Transform allowed_values string[] to { value, label }[]
        const options = (llm.allowed_values || []).map((val) => ({
          value: val,
          label: val,
        }));
        return (
          <SelectField
            {...commonProps}
            options={options}
          />
        );

      case 'textarea':
        return <TextAreaField {...commonProps} />;

      case 'date':
        return <DateField {...commonProps} />;

      case 'number':
        return <NumberField {...commonProps} />;

      case 'text':
      case 'email':
      case 'tel':
      default:
        // Default to text field
        return <TextField {...commonProps} />;
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-8">
      {sections.map((section) => (
        <div key={section.sectionKey} className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
            {section.sectionLabel}
          </h3>
          <div className="flex flex-wrap -mx-2">
            {section.fields.map(({ fieldName, definition }) => {
              const width = definition.ui.width || 'full';
              const widthClass = WIDTH_CLASSES[width];
              return (
                <div key={fieldName} className={`${widthClass} px-2`}>
                  {renderField(fieldName, definition)}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      <div className="flex gap-4 pt-6 border-t border-gray-200">
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Enregistrement...' : 'Valider'}
        </button>
      </div>
    </form>
  );
}
